import { UploadCard } from '@/components/upload-card/UploadCard'
import { UploadCardStatus } from '@/components/upload-card/constants'
import { MedChronFileStatus } from './med-chron-file-status'

export { MedChronFileStatus }

type MedChronFileCardProps =
  // EXTRACTED
  | {
      fileName: string
      status: MedChronFileStatus.EXTRACTED
      numEvents: number // Required for displaying events extracted count
      errorMessage?: never
      onRemove?: never
      onClick?: () => void
    }
  // All other statuses
  | {
      fileName: string
      status: Exclude<MedChronFileStatus, MedChronFileStatus.EXTRACTED>
      numEvents?: never
      errorMessage?: string
      onRemove: () => void
      onClick?: () => void
    }

const eventsText = (count: number): string => {
  if (count === 0) return 'No events extracted'
  if (count === 1) return '1 event extracted'
  return `${count} events extracted`
}

// Only these statuses represent a file the viewer can open, and only PDFs are viewable.
const kClickableStatuses: MedChronFileStatus[] = [MedChronFileStatus.PROCESSING, MedChronFileStatus.COMPLETED, MedChronFileStatus.EXTRACTED]

/** An extracted file that produced no events gets the warning treatment rather than the success one. */
function uploadCardStatus(status: MedChronFileStatus, numEvents?: number): UploadCardStatus {
  switch (status) {
    case MedChronFileStatus.UPLOADING:
      return UploadCardStatus.Uploading
    case MedChronFileStatus.PROCESSING:
      return UploadCardStatus.Processing
    case MedChronFileStatus.COMPLETED:
      return UploadCardStatus.Complete
    case MedChronFileStatus.EXTRACTED:
      return numEvents === 0 ? UploadCardStatus.Warning : UploadCardStatus.Complete
    case MedChronFileStatus.ERROR:
      return UploadCardStatus.Error
  }
}

/**
 * A pending-upload row on the chronology Add Files surface. Thin wrapper over the shared UploadCard
 * that supplies the med-chron events-extracted copy and open rules.
 */
export default function MedChronFileCard(props: MedChronFileCardProps) {
  const { fileName, status, errorMessage, numEvents, onClick } = props
  const isClickable = Boolean(onClick) && kClickableStatuses.includes(status) && fileName.toLowerCase().endsWith('.pdf')
  const isUploading = status === MedChronFileStatus.UPLOADING

  return (
    <UploadCard
      status={uploadCardStatus(status, numEvents)}
      filename={fileName}
      message={
        status === MedChronFileStatus.EXTRACTED
          ? eventsText(numEvents)
          : status === MedChronFileStatus.ERROR
          ? errorMessage || 'Document could not be read'
          : undefined
      }
      // The remove action cancels the transfer while it is still running, and dismisses the row after.
      onCancel={isUploading ? props.onRemove : undefined}
      dismissLabel={`Remove ${fileName}`}
      onDismiss={!isUploading ? props.onRemove : undefined}
      onRowClick={isClickable ? onClick : undefined}
    />
  )
}
